import { IconNames } from '@dixa/lib-shared-habla';
import { NotificationT } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import sortBy from 'lodash/sortBy';

import {
  Notification,
  UserMentionedNotificationParams,
} from 'apis/notifications';
import { getShortHumanReadableTimeFromNow } from 'utils/getShortHumanReadableTimeFromNow';
import { getUserDisplayName } from 'utils/user';

import {
  HistoricalNotification,
  PresentationData,
} from '../notificationsPage.types';

type AllUsers = { user: User }[];

const transformPersisted = (
  {
    payload_data: payload,
    created_at: createdAt,
    notification_id: id,
  }: Notification,
  allUsers: AllUsers,
): HistoricalNotification => {
  let icon: IconNames = 'sync';
  let status: PresentationData['status'] = 'default';
  let title = '';

  switch (payload?.type) {
    case 'bulkaction:started':
    case 'bulkaction:finished':
      const { subject, notification_type } = payload.params;
      title = subject;

      if (notification_type === 'BulkActionSucceeded') {
        icon = 'done';
        status = 'success';
      } else if (notification_type === 'BulkActionFailed') {
        icon = 'exclamation';
        status = 'danger';
      } else if (notification_type === 'BulkActionPartialFailed') {
        icon = 'exclamation';
        status = 'warning';
      }
      break;
    case 'mention:user_mentioned':
      icon = 'at';
      title = makeUserMentionedNotificationTitle(
        payload.params.author_id,
        allUsers,
      );
      break;
    default:
      break;
  }

  return {
    id,
    payload,
    presentation: {
      title,
      icon,
      status,
      subText: getShortHumanReadableTimeFromNow(createdAt),
    },
    timestamp: new Date(createdAt).getTime(),
  };
};

const transformLocal = (
  notification: NotificationT,
): HistoricalNotification => {
  return {
    id: notification.id,
    presentation: {
      ...notification,
      subText: getShortHumanReadableTimeFromNow(notification.timestamp),
    },
    timestamp: new Date(notification.timestamp).getTime(),
  };
};

/** Creates a unique mention:user_mentioned notification id from existing fields. */
export const makeUserMentionedNotificationId = ({
  author_id,
  mentioned_on,
}: UserMentionedNotificationParams): string =>
  `${author_id}_${mentioned_on.note_id}`;

/** Creates a title for the mention:user_mentioned notification */
export const makeUserMentionedNotificationTitle = (
  authorId: string,
  allUsers: AllUsers,
): string => {
  const { user: author } =
    allUsers.find(({ user }) => user.id === authorId) ?? {};
  return `${
    author ? getUserDisplayName(author, 'Unknown user') : 'Unknown user'
  } mentioned you`;
};

/**
 * Transforms local and persisted notifications into a presentation layer
 * notifications.
 */
export const transformAllNotifications = (
  local: readonly NotificationT[],
  persisted: readonly Notification[],
  allUsers: AllUsers,
): HistoricalNotification[] => {
  return sortBy(
    [
      ...local.map(transformLocal),
      ...persisted.map((notification) =>
        transformPersisted(notification, allUsers),
      ),
    ],
    ['timestamp'],
  ).reverse();
};
